import type { ApiErrorBody, LoginResponse, SessionUser } from '@bidmanager/shared';
import { reportIssue, setAccessTokenSource } from './log';

/**
 * Browser API client.
 *
 * Unlike the desktop app — where tokens live in the main process and the
 * renderer never holds one — a browser has nowhere better than memory plus
 * `localStorage` for the refresh token. That is a deliberate, bounded tradeoff:
 * the portal is a first-party origin with a strict CSP and no third-party
 * scripts, so the realistic threat is XSS in our own code rather than a hostile
 * script already on the page.
 *
 * The access token is kept **in memory only**. A page reload silently exchanges
 * the refresh token for a new one, so the short-lived credential is never
 * written anywhere a stored-XSS payload could read it later.
 */

const REFRESH_KEY = 'bidmanager:refresh';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

// So a failure reported by the portal carries the client it happened to — the
// first question anyone asks about a bug report. Read lazily on each report,
// since the token is rotated in place.
setAccessTokenSource(() => accessToken);

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

function setSession(response: LoginResponse): void {
  accessToken = response.accessToken;
  localStorage.setItem(REFRESH_KEY, response.refreshToken);
}

export function clearSession(): void {
  accessToken = null;
  localStorage.removeItem(REFRESH_KEY);
}

export async function login(
  email: string,
  password: string,
  clientSlug?: string,
): Promise<SessionUser> {
  const response = await send('POST', '/auth/login', {
    email,
    password,
    ...(clientSlug ? { clientSlug } : {}),
  });
  const body = (await parse(response)) as LoginResponse;
  setSession(body);
  return body.user;
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    await send('POST', '/auth/logout', { refreshToken }).catch(() => undefined);
  }
  clearSession();
}

/** Restore a session from the stored refresh token on page load. */
export async function restore(): Promise<SessionUser | null> {
  if (!getRefreshToken()) return null;
  const ok = await refreshSession();
  if (!ok) return null;
  try {
    const me = (await request<{ id: string; clientId: string; role: SessionUser['role'] }>(
      'GET',
      '/auth/me',
    )) as { id: string; clientId: string; role: SessionUser['role'] };
    return {
      id: me.id,
      clientId: me.clientId,
      clientName: '',
      email: '',
      username: '',
      role: me.role,
    };
  } catch {
    clearSession();
    return null;
  }
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  try {
    let response = await send(method, path, body);

    if (response.status === 401 && getRefreshToken()) {
      if (await refreshSession()) response = await send(method, path, body);
    }

    return (await parse(response)) as T;
  } catch (error) {
    reportApiFailure(method, path, error);
    throw error;
  }
}

/**
 * Report only what the user cannot act on: the API being unreachable, and 5xx.
 * A 4xx is the server telling this client it asked wrongly — it is already
 * shown in the UI, and recording every validation failure would bury the
 * entries that matter.
 */
function reportApiFailure(method: string, path: string, error: unknown): void {
  const status = error instanceof ApiError ? error.status : 0;
  if (status !== 0 && status < 500) return;
  reportIssue('api', error, {
    code: error instanceof ApiError ? `${status} ${error.body.code}` : 'NETWORK',
    context: { method, path, status },
  });
}

async function send(method: string, path: string, body?: unknown): Promise<Response> {
  return fetch(`/api${path}`, {
    method,
    headers: {
      'content-type': 'application/json',
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

function refreshSession(): Promise<boolean> {
  // Shared across concurrent 401s. Two parallel rotations of the same refresh
  // token look like reuse to the server, and it revokes the whole family for
  // that — signing the user out mid-task.
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  const response = await send('POST', '/auth/refresh', { refreshToken });
  if (!response.ok) {
    clearSession();
    return false;
  }
  setSession((await response.json()) as LoginResponse);
  return true;
}

async function parse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  const parsed: unknown = text.length > 0 ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      (parsed ?? {
        code: 'VALIDATION_FAILED',
        message: `Request failed (${response.status})`,
      }) as ApiErrorBody,
    );
  }
  return parsed;
}

/**
 * Rewrites an absolute presigned storage URL (the API's own origin) into a
 * same-origin path, so the browser fetches it through this app's `/api/*`
 * proxy instead of hitting the API's origin directly. Necessary when that
 * origin is behind ngrok's free tier, which shows an HTML interstitial to
 * any direct browser request — see functions/api/[[path]].ts, which forwards
 * the header that bypasses it. A direct link would otherwise render as a
 * broken image (screenshots) or an extra click-through page (PDF downloads).
 */
export function proxiedUrl(absoluteUrl: string): string {
  const url = new URL(absoluteUrl);
  return `${url.pathname}${url.search}`;
}

/**
 * Only http(s) links are rendered as hrefs. The server already refuses anything
 * else when a link is saved; this keeps a `javascript:` URL from ever becoming
 * clickable if one got past it.
 */
export function isWebLink(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

export function errorCode(error: unknown): string | undefined {
  return error instanceof ApiError ? error.body.code : undefined;
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.';
}
